"use client";

import {
  WorkplaceRequestElement,
  WorkplaceRequestTypeStatus
} from "@gettactic/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from "@gettactic/components/src/primitives/dropdown-menu";
import { capitalize, cn } from "@gettactic/helpers";
import { Check, ChevronDown } from "lucide-react";
import React, { useEffect, useState } from "react";

type BaseStatus = "open" | "pending" | "closed";

const GROUPS: { base: BaseStatus; label: string }[] = [
  { base: "open", label: "Open" },
  { base: "pending", label: "In progress" },
  { base: "closed", label: "Closed" }
];

// Dot color by base status: open = blue, in progress = amber, resolved = green.
const dotColor = (base: string): string =>
  base === "pending"
    ? "bg-amber-500"
    : base === "closed"
      ? "bg-green-500"
      : "bg-indigo-500";

/** What the trigger has to render: the status as it currently reads. */
export interface SubStatusSelectState {
  /** Sub-status name, or the capitalized base status when the type has none. */
  label: string;
  /** The base status behind it (open / pending / closed), for tinting. */
  baseStatus: string;
}

interface SubStatusSelectProps {
  request: WorkplaceRequestElement;
  typeStatuses: WorkplaceRequestTypeStatus[];
  disabled?: boolean;
  /**
   * Replaces the default full-width select button — used where the status has
   * to read as a pill (the linked-requests rows) rather than a form control.
   */
  renderTrigger?: (state: SubStatusSelectState) => React.ReactElement;
  /**
   * Persists the new status. `value` is a sub-status id (when sub-statuses exist) or a base
   * status string — the backend's `status` field accepts either.
   */
  onStatusChange: (
    requestId: string,
    value: string,
    comment?: string
  ) => void | Promise<void>;
}

export const SubStatusSelect: React.FC<SubStatusSelectProps> = ({
  request,
  typeStatuses,
  disabled = false,
  renderTrigger,
  onStatusChange
}) => {
  // Optimistic selection for instant feedback; cleared once fresh data arrives.
  const [optimisticSubId, setOptimisticSubId] = useState<string | null>(null);
  useEffect(() => {
    setOptimisticSubId(null);
  }, [request.sub_status?.id]);

  const currentSubId = optimisticSubId ?? request.sub_status?.id ?? null;
  const currentSub = typeStatuses.find((s) => s.id === currentSubId) ?? null;
  const currentBase = (currentSub?.base_status ?? request.status) as string;
  const label = currentSub?.name ?? capitalize(request.status);

  const selectSub = async (sub: WorkplaceRequestTypeStatus) => {
    setOptimisticSubId(sub.id);
    try {
      await onStatusChange(request.id, sub.id);
    } catch {
      // Persisting failed; drop the optimistic value so the UI falls back to
      // the last saved status instead of showing one that wasn't saved.
      setOptimisticSubId(null);
    }
  };

  // A bucket left on its default moves the request to the base status. Picking
  // one means "no sub-status", so the optimistic sub-status is dropped right
  // away: the menu now mixes both kinds, and `request.sub_status?.id` stays
  // undefined for a request that never had one, so the effect above would
  // never fire to clear it. The rejection still has to be caught: callers may
  // rethrow so a caller-side optimistic value can roll back, and they report
  // the failure themselves.
  const selectBase = async (base: BaseStatus) => {
    setOptimisticSubId(null);
    try {
      await onStatusChange(request.id, base);
    } catch {
      // Reported by the caller.
    }
  };

  // Sub-statuses replace the base one bucket by bucket, not menu-wide: naming
  // "WIP" inside In progress must not take Open and Closed off the menu, since
  // nothing else would be left to move the request to.
  const grouped = GROUPS.map((group) => ({
    ...group,
    statuses: typeStatuses
      .filter((s) => s.base_status === group.base)
      .sort((a, b) => a.order - b.order)
  }));
  // Once any bucket names its own statuses the menu reads as buckets, so all
  // three get their header: a bucket left on its default would otherwise look
  // like it belonged to the section above it. A type that names none stays the
  // plain three-item list it has always been.
  const hasSubStatuses = typeStatuses.length > 0;

  return (
    <div className={renderTrigger ? undefined : "w-full"}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          {renderTrigger ? (
            renderTrigger({ label, baseStatus: currentBase })
          ) : (
            <button
              type="button"
              disabled={disabled}
              className="flex h-9 w-full items-center justify-between gap-2 rounded-brand border border-neutral-200 bg-white px-3 font-medium text-neutral-900 text-sm hover:border-neutral-300 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span
                  className={cn(
                    "h-2 w-2 shrink-0 rounded-full",
                    dotColor(currentBase)
                  )}
                />
                <span className="truncate">{label}</span>
              </span>
              <ChevronDown className="h-4 w-4 shrink-0 text-neutral-400" />
            </button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={renderTrigger ? "end" : "start"}
          // The default select spans its trigger; a pill trigger is far too
          // narrow to hang a status list off, so that one gets a fixed width.
          className={
            renderTrigger
              ? "w-56"
              : "w-[var(--radix-dropdown-menu-trigger-width)]"
          }
        >
          {grouped.map((group) => (
            <React.Fragment key={group.base}>
              {hasSubStatuses ? (
                <DropdownMenuLabel className="text-[10px] text-neutral-400 uppercase tracking-wide">
                  {group.label}
                </DropdownMenuLabel>
              ) : null}
              {group.statuses.length === 0 ? (
                <DropdownMenuItem
                  onClick={() => selectBase(group.base)}
                  className="flex items-center justify-between gap-2"
                >
                  <span className="flex items-center gap-2">
                    <span
                      className={cn(
                        "h-2 w-2 rounded-full",
                        dotColor(group.base)
                      )}
                    />
                    {group.label}
                  </span>
                  {!currentSubId && request.status === group.base ? (
                    <Check className="h-4 w-4 text-neutral-500" />
                  ) : null}
                </DropdownMenuItem>
              ) : (
                group.statuses.map((sub) => (
                  <DropdownMenuItem
                    key={sub.id}
                    onClick={() => selectSub(sub)}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className={cn(
                          "h-2 w-2 rounded-full",
                          dotColor(sub.base_status)
                        )}
                      />
                      {sub.name}
                    </span>
                    {sub.id === currentSubId ? (
                      <Check className="h-4 w-4 text-neutral-500" />
                    ) : null}
                  </DropdownMenuItem>
                ))
              )}
            </React.Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
